/** Variantes de un SKU físico (backend: VariantesDeSku). Las cantidades son parte del stock físico; lo no repartido es «Sin asignar». */
export interface VarianteDeSku {
  id: number;
  nombre: string;
  colorHex: string | null;
  cantidad: number;
  activo: boolean;
  publicaciones: Array<{ mlaId: string; variantId: string | null; skuInterno: string }>;
}

export interface VariantesDeSku {
  skuInterno: string;
  stockPorVarianteEnMl: boolean;
  fisico: number;
  /** Unidades en cuarentena (están en el físico, pero no son de ninguna variante ni de Sin asignar). */
  cuarentena?: number;
  asignadas: number;
  sinAsignar: number;
  aviso: string | null;
  variantes: VarianteDeSku[];
}

/** Publicación tal como la trae la ficha del SKU; sólo un vínculo con publicación sincronizada y `variantId` es una variación real. */
export interface PublicacionParaAviso { mlaId: string; variantId: string | null; origen?: string; publicacion?: unknown | null }
export interface PublicacionesParaAviso { directas: PublicacionParaAviso[]; indirectas: PublicacionParaAviso[] }

/**
 * Variaciones de ML que, con el switch prendido, publicarían 0 por no tener variante ligada (regla de stock-bom.service).
 * `seguras`: las publicaciones directas del SKU. `posibles`: las que llegan por packs o combos (indirectas); el backend sólo
 * las baja a este físico si el SKU es un pack de un único componente, y eso no se sabe acá.
 */
export function variacionesSinVariante(
  publicaciones: PublicacionesParaAviso | undefined, variantes: VarianteDeSku[],
): { seguras: string[]; posibles: string[] } {
  const ligadas = new Set(variantes.flatMap((v) => v.publicaciones.map((p) => `${p.mlaId}/${p.variantId}`)));
  const sueltas = (lista: PublicacionParaAviso[] = []) => [...new Set(lista
    .filter((p) => p.variantId && p.origen === 'vinculo' && p.publicacion != null && !ligadas.has(`${p.mlaId}/${p.variantId}`))
    .map((p) => `${p.mlaId}/${p.variantId}`))];
  const seguras = sueltas(publicaciones?.directas);
  return { seguras, posibles: sueltas(publicaciones?.indirectas).filter((k) => !seguras.includes(k)) };
}

/** Reparto de la cantidad de una línea del remito: `varianteId` → cantidad tal como se tecleó. Lo no repartido queda «Sin asignar». */
export type Reparto = Record<number, string>;

export interface LineaRemito {
  cantidad: string | number; cantidadRotura?: string | number; sku?: string | null; ordenCompraItemId?: number | null; reparto?: Reparto;
}
export interface ItemGuardado {
  cantidad: string | number; cantidadRotura?: string | number; sku?: string | null; ordenCompraItemId?: number | null; varianteId?: number | null;
}

const num = (v: string | number | undefined | null) => parseFloat(String(v ?? '')) || 0;
/** Las cantidades son decimales: se redondea a 4 como el backend (redondear4) para que 0.1 + 0.2 sea 0.3. */
const r4 = (n: number) => Math.round(n * 1e4) / 1e4;
const entradas = (reparto: Reparto) => Object.entries(reparto).map(([id, c]) => [Number(id), Math.max(0, num(c))] as const).sort((a, b) => a[0] - b[0]);

export function resumenReparto(recibido: number, reparto: Reparto): { repartidas: number; sinAsignar: number; excede: boolean } {
  const repartidas = r4(entradas(reparto).reduce((s, [, c]) => s + c, 0));
  return { repartidas, sinAsignar: r4(recibido - repartidas), excede: repartidas > r4(recibido) };
}

/** Una línea con reparto pasa a varios ítems: primero el resto sin variante y después uno por variante. Las roturas van en el primero. */
export function expandirLinea<T extends LineaRemito>(linea: T): Array<Omit<T, 'reparto'> & { varianteId?: number }> {
  const { reparto, ...base } = linea;
  const conCantidad = reparto ? entradas(reparto).filter(([, c]) => c > 0) : [];
  if (conCantidad.length === 0) return [base];
  const resto = r4(num(linea.cantidad) - conCantidad.reduce((s, [, c]) => s + c, 0));
  const partes: Array<{ cantidad: number; varianteId?: number }> = [
    ...(resto > 0 ? [{ cantidad: resto }] : []),
    ...conCantidad.map(([varianteId, cantidad]) => ({ cantidad, varianteId })),
  ];
  const rotura = num(linea.cantidadRotura);
  return partes.map((p, n) => ({ ...base, ...p, cantidadRotura: n === 0 ? rotura : 0 }));
}

/** Inverso de `expandirLinea`: un ítem con variante se suma a la línea anterior del mismo SKU y la misma línea de OC. */
export function agruparItemsConReparto<T extends ItemGuardado>(items: T[]): Array<T & { reparto?: Reparto }> {
  const salida: Array<T & { reparto?: Reparto }> = [];
  for (const item of items) {
    if (item.varianteId == null) { salida.push({ ...item }); continue; }
    const { varianteId, ...resto } = item;
    const prev = salida[salida.length - 1];
    const mismaLinea = prev && (prev.sku ?? null) === (item.sku ?? null) && (prev.ordenCompraItemId ?? null) === (item.ordenCompraItemId ?? null);
    const destino = mismaLinea ? prev : ({ ...resto, cantidad: '0', cantidadRotura: undefined, reparto: {} } as unknown as T & { reparto: Reparto });
    const reparto = destino.reparto ?? {};
    reparto[varianteId] = String(num(reparto[varianteId]) + num(item.cantidad));
    const rotura = num(destino.cantidadRotura) + num(item.cantidadRotura);
    Object.assign(destino, { reparto, cantidad: String(num(destino.cantidad) + num(item.cantidad)) });
    if (rotura > 0) destino.cantidadRotura = String(rotura); else delete destino.cantidadRotura;
    if (!mismaLinea) salida.push(destino);
  }
  return salida;
}

/** Pone el SKU de una línea; si cambia, el reparto (ids de variantes del SKU anterior) ya no vale. */
export function conSku<T extends { sku?: string | null; reparto?: Reparto }>(item: T, sku: string): T {
  return (item.sku ?? '') === sku ? item : { ...item, sku, reparto: undefined };
}

/** Fila del maestro de variantes (GET /api/v3/catalogo/variantes): todas las variantes de todos los SKU físicos. */
export interface FilaMaestroVariante { id: number; skuInterno: string; nombre: string; colorHex: string | null; cantidad: number; activo: boolean }

/** Respuesta de GET /api/v3/catalogo/:sku/variantes/:id/vinculos: lo que se ve afectado al renombrar o desactivar la variante. */
export interface VinculosDeVariante {
  publicaciones: Array<{ mlaId: string; variantId: string | null; skuVenta: string; titulo: string | null }>;
  combos: Array<{ skuInterno: string; descripcion: string | null }>;
}

/** Respuesta de POST /api/v3/catalogo/variantes/importar (backend: PlanImportVariantes + dryRun). `fila` es la referencia que da el backend. */
export interface ResultadoImportVariantes {
  dryRun: boolean;
  nuevas: Array<{ fila: number; skuInterno: string; nombre: string; nombreNormalizado: string; colorHex: string | null }>;
  cambios: Array<{ fila: number; id: number; skuInterno: string; nombre: string; colorAnterior: string | null; colorNuevo: string | null; reactivar: boolean }>;
  errores: Array<{ fila: number; motivo: string }>;
  sinCambios: number;
}

/** Orden recomendado para trabajar con variantes: lo muestran la pantalla Variantes y el instructivo. Los pasos se numeran seguidos entre etapas. */
export const PASOS_VARIANTES: Array<{ etapa: string; pasos: string[] }> = [
  {
    etapa: 'Etapa 1 · Mercado Libre no cambia',
    pasos: [
      'Cargá el maestro una sola vez: Configuración → Mercado Libre y catálogo → «Traer planilla ahora» → «Variantes fuera del maestro» → «Agregar al maestro». Después, en Configuración → Variantes, desactivá lo que no sea una variante real.',
      'Repartí cada remito nuevo por variante al recibirlo.',
      'Repartí el stock viejo de a poco, empezando por los SKU que más venden: contalo por color dentro de un conteo de inventario («Contar por color», o la columna Variante de la planilla). Al confirmar, cada color queda en lo contado y el resto queda en «Sin asignar». «Reasignar variante» (Movimientos de stock) sirve para corregir a mano.',
      'Las ventas de Mercado Libre descuentan su variante solas.',
    ],
  },
  {
    etapa: 'Etapa 2 · Un SKU a la vez',
    pasos: [
      'Cuando un SKU queda con «Sin asignar» en 0, revisá su ficha y la vista previa del SKU en Mercado Libre (Base y publicaciones), y prendé «Publicar en Mercado Libre el stock de cada variante». Probá primero con uno.',
    ],
  },
];
